import React, {useEffect, useState, useLayoutEffect, useCallback} from 'react';
import {
  useFocusEffect,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import {
  View,
  Platform,
  ScrollView,
  Image,
  Text,
  useWindowDimensions,
} from 'react-native';
import {StyleSheet} from 'react-native';
import _ from 'lodash';

import {RNEButton, RNEIconButton, RNEText, SVGIcon} from '../../components';
import {LogoLight} from './../../images/Image';
import {TouchableWithoutFeedback} from 'react-native-gesture-handler';
import {
  useDownloadMedicalCertificate,
  useGetMedicalCertificate,
} from '../../server/react-query/useMedicalAttachment';
import {formatDateString, formatToMessageTime} from '../../utils/date.utils';
import {colorPalette, colors} from '../../../core/config/color.config';
import HeaderIconRight from '../navigation/HeaderIconRight';
import {useDownloader} from '../../hooks/downloader/useDownloader';
import moment from 'moment';
import {width, height, totalSize} from 'react-native-dimension';
import LightAppLogo from '../../../assets/logo/logo-light.png';
import useConversation from '../../hooks/conversation/useConversation';
import {useGetConsultationDetail} from '../../server/react-query/useConsultation';
import {Icon} from 'react-native-elements'; // rne-netzon replaced
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  clamp,
} from 'react-native-reanimated';
import {GestureDetector, Gesture} from 'react-native-gesture-handler';
import {usePullDownRefreshContext} from '../../stores/pullDownRefreshStore';

export interface ViewMedicalCertificateViewProps {
  onClose: () => void;
  id: string;
  subject?: string;
}

const ViewMedicalCertificateView = (props: ViewMedicalCertificateViewProps) => {
  const route = useRoute();
  const navigation = useNavigation();

  const [id, setId] = useState();
  const [navSubject, setNavSubject] = useState();
  const [age, setAge] = useState<number | null>(null);

  const {data, isFetching, refetch} = useGetMedicalCertificate(
    Platform.OS === 'web' ? props.id : id!,
    Platform.OS,
    Platform.OS === 'web' ? props.subject! : navSubject!,
  );

  const {data: webBlob, refetch: download} = useDownloadMedicalCertificate(
    Platform.OS === 'web' ? props.id : id!,
    Platform.OS,
    Platform.OS === 'web' ? props.subject : navSubject,
  );

  const {data: consultationDetails} = useGetConsultationDetail(
    {serviceRequestId: data?.basedOnId},
    {enabled: !!data?.basedOnId},
  );

  const {initialized, getConversation, conversation} = useConversation();

  const [procedureId, setProcedureId] = useState(null);
  const [practitionerInitialRespondDate, setPractitionerInitialRespondDate] =
    useState('');

  const {setCanRefresh} = usePullDownRefreshContext();

  useEffect(() => {
    if (initialized) {
      if (procedureId) {
        getConversation(procedureId);
      }
    }
  }, [initialized, procedureId]);

  useEffect(() => {
    const initConversation = async () => {
      if (conversation) {
        const msgs = await conversation.getMessages(1000);
        const firstDoctorMessage = msgs.items.find(
          msg => msg.author === consultationDetails?.performer?.practitionerId,
        );

        if (firstDoctorMessage) {
          if (_.isEmpty(firstDoctorMessage.attributes)) {
            setPractitionerInitialRespondDate(
              formatToMessageTime(
                firstDoctorMessage.dateCreated,
                'MMM dd, yyyy',
              ),
            );
          } else {
            setPractitionerInitialRespondDate(
              formatToMessageTime(
                firstDoctorMessage.attributes.CreatedOn,
                'MMM dd, yyyy',
              ),
            );
          }
        } else {
          setPractitionerInitialRespondDate(
            formatToMessageTime(data?.issued, 'MMM dd, yyyy'),
          );
        }
      }
    };
    initConversation();
  }, [conversation, consultationDetails]);

  useEffect(() => {
    if (consultationDetails) {
      setProcedureId(consultationDetails?.procedure?.procedureId);
    }
  }, [consultationDetails]);

  // console.log(data,'data')

  const medicalCertificateConfig = {
    web: {
      fileName: 'Medical Certificate',
      data: webBlob,
      download: download,
    },
    mobile: {
      download: download,
    },
  };

  const {downloadAttachment} = useDownloader(medicalCertificateConfig);

  const onDownloadClick = () => {
    downloadAttachment();
  };

  const getFullName = humanName => {
    const givenNames = Array.isArray(humanName?.given)
      ? humanName.given.join(' ').trim()
      : '';

    const familyName = humanName?.family ?? '';

    return [givenNames, familyName].filter(Boolean).join(' ');
  };

  const getAgeFromDOB = dobString => {
    const dob = new Date(dobString);
    const today = new Date();

    let age = today.getFullYear() - dob.getFullYear();
    const hasBirthdayPassedThisYear =
      today.getMonth() > dob.getMonth() ||
      (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate());

    if (!hasBirthdayPassedThisYear) {
      age -= 1;
    }

    return age;
  };

  const getSpecialtiesCommaSeparated = (practitionerRole = []) => {
    return practitionerRole
      .map(role => role?.specialty?.text)
      .filter(Boolean) // remove undefined/null
      .join(', ');
  };

  useEffect(() => {
    const params = route.params as any;

    if (params && params.id) {
      setId(params.id);
    }

    if (params && params.subject) {
      setNavSubject(params.subject);
    }
  }, [route.params]);

  useEffect(() => {
    if (id && navSubject) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navSubject]);

  useLayoutEffect(() => {
    if (Platform.OS !== 'web') {
      navigation.setOptions({
        headerRight: () => (
          // <HeaderIconRight iconName="download-down" onClick={onDownloadClick} />
          <HeaderIconRight noicon={true} />
        ),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation]);

  useEffect(() => {
    if (data?.subject.birthdate) {
      const birthdate = new Date(data.subject.birthdate);
      const ageDifMs = Date.now() - birthdate.getTime();
      const ageDate = new Date(ageDifMs);
      const calculatedAge = Math.abs(ageDate.getUTCFullYear() - 1970);
      setAge(calculatedAge);
    }
  }, [data]);

  const {width: screenWidth, height: screenHeight} = useWindowDimensions();

  const scale = useSharedValue(1);
  const initialScale = useSharedValue(1);

  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);

  const lastTranslateX = useSharedValue(0);
  const lastTranslateY = useSharedValue(0);

  const focalX = useSharedValue(0);
  const focalY = useSharedValue(0);

  const pinchGesture = Gesture.Pinch()
    .onBegin(e => {
      initialScale.value = scale.value;
      focalX.value = e.focalX;
      focalY.value = e.focalY;
    })
    .onUpdate(e => {
      const newScale = clamp(e.scale * initialScale.value, 1, 3);

      const scaleDiff = newScale - initialScale.value;

      const originX = focalX.value - screenWidth / 2;
      const originY = focalY.value - screenHeight / 2;

      const moveX = (originX * scaleDiff) / newScale;
      const moveY = (originY * scaleDiff) / newScale;

      const boundX = ((newScale - 1) * screenWidth) / 2;
      const boundY = ((newScale - 1) * screenHeight) / 2;

      scale.value = newScale;

      translateX.value = clamp(lastTranslateX.value - moveX, -boundX, boundX);
      translateY.value = clamp(lastTranslateY.value - moveY, -boundY, boundY);
    })
    .onEnd(() => {
      lastTranslateX.value = translateX.value;
      lastTranslateY.value = translateY.value;
    });

  const panGesture = Gesture.Pan()
    .onUpdate(e => {
      if (scale.value > 1) {
        const boundX = ((scale.value - 1) * screenWidth) / 2;
        const boundY = ((scale.value - 1) * screenHeight) / 2;

        translateX.value = clamp(
          lastTranslateX.value + e.translationX,
          -boundX,
          boundX,
        );
        translateY.value = clamp(
          lastTranslateY.value + e.translationY,
          -boundY,
          boundY,
        );
      }
    })
    .onEnd(() => {
      if (scale.value > 1) {
        lastTranslateX.value = translateX.value;
        lastTranslateY.value = translateY.value;
      }
    });

  const doubleTapGesture = Gesture.Tap()
    .numberOfTaps(2)
    .onEnd(() => {
      scale.value = withTiming(1);
      translateX.value = withTiming(0);
      translateY.value = withTiming(0);
      lastTranslateX.value = 0;
      lastTranslateY.value = 0;
    });

  const gesture = Gesture.Simultaneous(
    Gesture.Race(doubleTapGesture, pinchGesture),
    panGesture,
  );

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [
      {translateX: translateX.value},
      {translateY: translateY.value},
      {scale: scale.value},
    ],
  }));

  const [scrollViewHeight, setScrollViewHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  useFocusEffect(
    useCallback(() => {
      if (scrollViewHeight > 0 && contentHeight > 0) {
        const isScrollable = contentHeight > scrollViewHeight;

        if (!isScrollable) {
          setCanRefresh(true); // always allow pull-down if not scrollable
        } else {
          setCanRefresh(scrollY <= 0); // only allow when scrolled to top
        }
      }
    }, [scrollViewHeight, contentHeight, scrollY, setCanRefresh]),
  );
  const handleScroll = (e: any) => {
    const y = e.nativeEvent.contentOffset.y;
    setScrollY(y);

    const isScrollable = contentHeight > scrollViewHeight;

    if (!isScrollable) {
      setCanRefresh(true); // still always true if no scroll
    } else {
      setCanRefresh(y <= 0); // only true when back at the very top
    }
  };

  return (
    <>
      {Platform.OS === 'web' ? (
        <View style={webStyles.mainContainer}>
          <View style={webStyles.headerContainer}>
            <Image style={webStyles.logo} source={LogoLight} />
            <View style={webStyles.rightIcons}>
              <TouchableWithoutFeedback>
                <RNEIconButton
                  icon={<SVGIcon name="download-down" size={28} />}
                  type="clear"
                  size={28}
                  onPress={onDownloadClick}
                  containerStyle={{borderRadius: 0}}
                />
              </TouchableWithoutFeedback>
              <TouchableWithoutFeedback>
                <RNEIconButton
                  icon={<SVGIcon name="close-gray-71" size={36} />}
                  type="clear"
                  size={36}
                  onPress={props.onClose}
                />
              </TouchableWithoutFeedback>
            </View>
          </View>
          <ScrollView>
            <View style={webStyles.bodyContainer}>
              {data && (
                <>
                  <View style={webStyles.infoContainer}>
                    <RNEText
                      label={`Patient: ${data.subject.humanName.given[0]} ${data.subject.humanName.family}`}
                      variant="body"
                      weight="bold"
                      style={textWebStyle}
                    />
                    <RNEText
                      label={`Address: ${data.subject.location ?? '--'}`}
                      variant="body"
                      weight="bold"
                      style={textWebStyle}
                    />
                    <RNEText
                      label={`Age: ${age ?? '--'}`}
                      variant="body"
                      weight="bold"
                      style={textWebStyle}
                    />
                    <RNEText
                      label={`Sex: ${
                        data.subject.sex
                          ? data.subject.sex.charAt(0).toUpperCase() +
                            data.subject.sex.slice(1)
                          : '--'
                      }`}
                      variant="body"
                      weight="bold"
                      style={textWebStyle}
                    />
                  </View>
                </>
              )}
              <View style={webStyles.titleContainer}>
                <RNEText
                  label="Medical Certificate"
                  variant="title"
                  weight="bold"
                  color="accent"
                />
                <View style={webStyles.spinnerContainer}>
                  <RNEButton
                    height={20}
                    loadingProps={loadingPropsStyle}
                    loading={isFetching}
                  />
                </View>
              </View>
              {data && (
                <>
                  <View style={webStyles.infoContainer}>
                    <RNEText
                      label={`Date Issued: ${formatDateString(data.issued)}`}
                      variant="body"
                      style={textWebStyle}
                    />
                    {/* <RNEText
                      label={`Medical Certified ID: #${data.diagnosticReportId
                        .substring(0, 7)
                        .toUpperCase()}`}
                      variant="body"
                      style={textWebStyle}
                    /> */}
                  </View>
                  <View style={webStyles.certificateContainer}>
                    <RNEText
                      label={data.conclusion || ''}
                      variant="body"
                      style={textWebStyle}
                    />
                  </View>
                  <View style={webStyles.doctorContainer}>
                    <RNEText
                      label={`Dr. ${data.performer.humanName.given[0]} ${data.performer.humanName.family}`}
                      weight="bold"
                      style={doctorNameStyle}
                    />
                    <RNEText
                      label={
                        data.performer.organization[0].address?.city
                          ? `${data.performer.practitionerRole[0].specialty.text}, ${data.performer.organization[0].address?.city}`
                          : `${data.performer.practitionerRole[0].specialty.text}`
                      }
                      variant="body"
                    />
                    <RNEText
                      label={`License No: ${data.performer.qualification.identifier}`}
                      variant="body"
                    />
                  </View>
                </>
              )}
            </View>
          </ScrollView>
        </View>
      ) : (
        <GestureDetector gesture={gesture}>
          <ScrollView
            onScroll={handleScroll}
            onLayout={e => {
              setScrollViewHeight(e.nativeEvent.layout.height);
            }}
            onContentSizeChange={(contentWidth, contentHeight) => {
              setContentHeight(contentHeight);
            }}
            showsVerticalScrollIndicator={false}>
            <Animated.View style={[animatedStyle, mobileStyles.mainContainer]}>
              {/* Logo */}
              <View>
                <Image
                  source={LightAppLogo}
                  style={{width: width(30), height: width(10), opacity: 0.3}}
                  resizeMode="contain"
                />
              </View>

              {/* Body */}
              <View style={{padding: height(2), paddingTop: 0}}>
                <View
                  style={{
                    borderBottomColor: colors.black.transparent23,
                    borderBottomWidth: width(0.4),
                    paddingBottom: height(1.5),
                  }}>
                  <Text
                    style={{
                      fontSize: totalSize(2.2),
                      color: colors.midnightBlue.regular,
                      fontFamily: 'Quicksand-Bold',
                    }}>
                    Medical Certificate
                  </Text>
                </View>

                <View
                  style={{
                    marginTop: height(2),
                    borderBottomColor: colors.black.transparent23,
                    borderBottomWidth: width(0.4),
                    paddingBottom: height(1.5),
                  }}>
                  <View style={{flexDirection: 'row', gap: width(4)}}>
                    <Text
                      style={{
                        textAlign: 'right',
                        width: width(20),
                        fontSize: totalSize(1.5),
                        fontFamily: 'Quicksand-Bold',
                      }}>
                      Name:
                    </Text>
                    <Text
                      style={{
                        fontSize: totalSize(1.5),
                        fontFamily: 'Quicksand-Regular',
                      }}>
                      {data && getFullName(data?.subject?.humanName)}
                    </Text>
                  </View>

                  <View style={{flexDirection: 'row', gap: width(4)}}>
                    <Text
                      style={{
                        fontSize: totalSize(1.5),
                        textAlign: 'right',
                        width: width(20),
                        fontFamily: 'Quicksand-Bold',
                      }}>
                      Age:
                    </Text>
                    <Text
                      style={{
                        fontSize: totalSize(1.5),
                        fontFamily: 'Quicksand-Regular',
                      }}>
                      {data && getAgeFromDOB(data?.subject?.birthdate)}
                    </Text>
                  </View>

                  <View style={{flexDirection: 'row', gap: width(4)}}>
                    <Text
                      style={{
                        fontSize: totalSize(1.5),
                        textAlign: 'right',
                        width: width(20),
                        fontFamily: 'Quicksand-Bold',
                      }}>
                      Address:
                    </Text>
                    <Text
                      style={{
                        flexWrap: 'wrap',
                        width: width(60),
                        fontFamily: 'Quicksand-Regular',
                      }}>
                      {data && data?.subject?.location}
                    </Text>
                  </View>
                </View>

                <View style={{marginTop: height(2)}}>
                  <Text
                    style={{
                      fontSize: totalSize(1.5),
                      fontFamily: 'Quicksand-Medium',
                    }}>
                    This is to certify that{' '}
                    <Text
                      style={{
                        fontFamily: 'Quicksand-Bold',
                        textDecorationLine: 'underline',
                      }}>
                      {data?.subject?.humanName &&
                        getFullName(data?.subject?.humanName)}
                    </Text>{' '}
                    was examined on{' '}
                    <Text
                      style={{
                        fontFamily: 'Quicksand-Bold',
                        textDecorationLine: 'underline',
                      }}>
                      {practitionerInitialRespondDate}
                    </Text>{' '}
                    with the following findings:
                  </Text>
                </View>

                <View style={{marginTop: height(2), marginBottom: height(1.5)}}>
                  <Text
                    style={{
                      fontSize: totalSize(1.6),
                      fontFamily: 'Quicksand-SemiBold',
                      marginBottom: height(0.5),
                    }}>
                    Diagnosis:
                  </Text>
                  <View
                    style={{
                      flexDirection: 'row',
                      gap: width(0.5),
                      paddingLeft: width(4),
                      alignItems: 'center',
                    }}>
                    <View style={{width: width(4), justifyContent: 'center'}}>
                      <Icon
                        type="material-community"
                        name="circle"
                        size={totalSize(1)}
                        color={colorPalette.black}
                      />
                    </View>
                    <Text
                      style={{
                        fontSize: totalSize(1.6),
                        fontFamily: 'Quicksand-Medium',
                      }}>
                      {data?.diagnosis}
                    </Text>
                  </View>
                </View>

                <View style={{marginTop: height(2), marginBottom: height(1.5)}}>
                  <Text
                    style={{
                      fontSize: totalSize(1.6),
                      fontFamily: 'Quicksand-SemiBold',
                      marginBottom: height(0.5),
                    }}>
                    Remarks:
                  </Text>
                  <View
                    style={{
                      flexDirection: 'row',
                      gap: width(0.5),
                      paddingLeft: width(4),
                      alignItems: 'center',
                    }}>
                    <View style={{width: width(4), justifyContent: 'center'}}>
                      <Icon
                        type="material-community"
                        name="circle"
                        size={totalSize(1)}
                        color={colorPalette.black}
                      />
                    </View>
                    <Text
                      style={{
                        fontSize: totalSize(1.6),
                        fontFamily: 'Quicksand-Medium',
                      }}>
                      {data?.conclusion}
                    </Text>
                  </View>
                </View>

                {/* Signature */}
                <View>
                  {data?.performer?.signature && (
                    <View
                      style={{
                        flexDirection: 'row',
                        justifyContent: 'flex-end',
                        width: width(100),
                        marginBottom: -height(4),
                      }}>
                      <Image
                        style={{
                          width: width(45),
                          height: width(20),
                          marginRight: width(10),
                        }}
                        resizeMode="cover"
                        resizeMethod="resize"
                        source={{uri: data?.performer?.signature}}
                      />
                    </View>
                  )}
                  <View>
                    <Text
                      style={{
                        textAlign: 'right',
                        fontSize: totalSize(1.8),
                        fontFamily: 'Quicksand-Bold',
                      }}>
                      {data?.performer?.humanName &&
                        getFullName(data?.performer?.humanName)}
                    </Text>
                    <Text
                      style={{
                        textAlign: 'right',
                        fontSize: totalSize(1.5),
                        fontFamily: 'Quicksand-Medium',
                        fontStyle: 'italic',
                      }}>
                      {data?.performer?.practitionerRole &&
                        getSpecialtiesCommaSeparated(
                          data?.performer?.practitionerRole,
                        )}
                    </Text>
                    <Text
                      style={{
                        textAlign: 'right',
                        fontSize: totalSize(1.5),
                        fontFamily: 'Quicksand-Medium',
                        fontStyle: 'italic',
                      }}>
                      License No: {data?.performer?.qualification?.identifier}
                    </Text>
                    {/* <Text style={{textAlign:'right', fontSize: totalSize(1.5), fontFamily:'Quicksand-Medium', fontStyle:'italic'}}>Issued On: {moment(data?.note[0]?.time).local().format('MMM DD, YYYY')}</Text> */}
                  </View>
                </View>
              </View>
            </Animated.View>
          </ScrollView>
        </GestureDetector>
      )}
    </>
  );
};

export default ViewMedicalCertificateView;

const doctorNameStyle = {
  fontSize: 24,
};

const textWebStyle = {
  lineHeight: 24,
};

const textMobileStyle = {
  lineHeight: 20,
};

const loadingPropsStyle = {
  color: colorPalette.primary,
};

const webStyles = StyleSheet.create({
  mainContainer: {
    marginHorizontal: 40,
    marginVertical: 20,
    flex: 1,
    width: '40vw',
    maxWidth: 700,
    minWidth: 440,
    maxHeight: '70vh',
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottomColor: 'rgba(229, 229, 229, 0.7)',
    borderBottomWidth: 1,
    paddingTop: 12,
    paddingBottom: 28,
  },
  logo: {
    width: 200,
    height: 44,
  },
  rightIcons: {
    flexDirection: 'row',
    alignSelf: 'center',
    justifyContent: 'space-between',
    width: 76,
  },
  closeIcon: {
    marginLeft: 28,
  },
  bodyContainer: {
    marginVertical: 40,
    marginHorizontal: 60,
  },
  infoContainer: {
    marginVertical: 24,
  },
  certificateContainer: {
    marginVertical: 28,
  },
  doctorContainer: {
    marginVertical: 24,
  },
  titleContainer: {
    flexDirection: 'row',
  },
  spinnerContainer: {
    alignSelf: 'flex-end',
    marginBottom: 8,
  },
});

const mobileStyles = StyleSheet.create({
  mainContainer: {
    width: '100%',
    // flex: 1,
    // height: height(95),
    paddingHorizontal: width(5),
    // paddingBottom: 52,
    paddingTop: height(1),
  },
  titleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  iconContainer: {
    paddingBottom: 12,
  },
  bodyContainer: {
    paddingVertical: 40,
  },
  buttonContainer: {
    marginRight: width(4),
  },
  buttonsContainer: {
    // position: 'absolute',
    marginTop: height(1),
    // left: 0,
    // right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  rightButtonsContainer: {
    flexDirection: 'row',
    // marginLeft: 20,
    flexWrap: 'wrap',
  },
});
